import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { toast } from "react-toastify";
import Table from "components/Table";
import { UserContext } from "context/user";
import { getOrders } from "api/Orders";
import Tabs from "components/organisms/Tabs";
import PaymentMethodCell from "../../components/PaymentMethodCell";
import StatusCell from "../../components/StatusCell";
import OrderIDCell from "../../components/OrderIDCell";
import DateOrderedCell from "../../components/DateOrderedCell";
import AmountCell from "../../components/AmountCell";
import "./index.scss";

const HighResOrdersContainer = () => {
  const { token } = useContext(UserContext);
  const fetchIdRef = useRef(0);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("");

  const fetchOrders = useCallback(
    async ({ pageSize, pageIndex }) => {
      if (!token) return;

      const orderStatus = [
        { status: "Cancelled", value: "cancelled" },
        { status: "New", value: "submitted" },
        { status: "Pending", value: "pending" },
        { status: "Capturing", value: "processing" },
        { status: "Completed", value: "done" },
      ];
      const filterStatus = orderStatus.find((order) => order.status === status);
      fetchIdRef.current += 1;
      const fetchId = fetchIdRef.current;
      if (fetchId === fetchIdRef.current && token) {
        const limit = pageSize;
        const skip = pageSize * pageIndex;
        setLoading(true);
        try {
          let query = `skip=${skip}&limit=${limit}`;
          if (filterStatus) {
            query += `&statuses=${filterStatus.value}`;
          }
          const ordersData = await getOrders(query, token);
          setOrders(ordersData.data);
          setPageCount(ordersData.pages.total_pages);
        } catch (e) {
          toast.error(e);
        }
        setLoading(false);
      }
    },
    [token, status]
  );

  const setFilter = (tab) => {
    setStatus(tab);
  };

  useEffect(() => {
    fetchOrders({ pageSize: 10, pageIndex: 0 });
  }, [fetchOrders]);

  const columns = useMemo(
    () => [
      {
        Header: "Order ID",
        accessor: "id",
        Cell: (props) => <OrderIDCell {...props} />,
      },
      {
        Header: "Date Ordered",
        accessor: "created_on",
        Cell: (props) => <DateOrderedCell {...props} />,
      },
      {
        Header: "Payment Method",
        accessor: "payment_method",
        Cell: (props) => <PaymentMethodCell {...props} />,
      },
      {
        Header: "Amount",
        accessor: "order_subtotal",
        Cell: (props) => <AmountCell {...props} />,
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: (props) => <StatusCell {...props} />,
      },
    ],
    []
  );

  const tabData = [
    {
      label: "All Orders",
      id: "all-orders",
      content: (
        <Table
          fetchData={fetchOrders}
          pageCount={pageCount}
          columns={columns}
          loading={loading}
          data={orders}
          className="-orders-table"
        />
      ),
    },
    {
      label: "New",
      id: "new",
      content: (
        <Table
          fetchData={fetchOrders}
          pageCount={pageCount}
          columns={columns}
          loading={loading}
          data={orders}
          className="-orders-table"
        />
      ),
    },
    {
      label: "Pending",
      id: "pending",
      content: (
        <Table
          fetchData={fetchOrders}
          pageCount={pageCount}
          columns={columns}
          loading={loading}
          data={orders}
          className="-orders-table"
        />
      ),
    },
    {
      label: "Capturing",
      id: "capturing",
      content: (
        <Table
          fetchData={fetchOrders}
          pageCount={pageCount}
          columns={columns}
          loading={loading}
          data={orders}
          className="-orders-table"
        />
      ),
    },
    {
      label: "Completed",
      id: "completed",
      content: (
        <Table
          fetchData={fetchOrders}
          pageCount={pageCount}
          columns={columns}
          loading={loading}
          data={orders}
          className="-orders-table"
        />
      ),
    },
    {
      label: "Cancelled",
      id: "cancelled",
      content: (
        <Table
          fetchData={fetchOrders}
          pageCount={pageCount}
          columns={columns}
          loading={loading}
          data={orders}
          className="-orders-table"
        />
      ),
    },
  ];

  return (
    <div data-testid="orders-page" className="orders-page">
      <section className="header-section">
        <h1 className="title">Orders</h1>
        <p className="description">
          Check your listed orders here. You can view receipt or follow up
          pending orders with this page.
        </p>
      </section>
      <div className="actions-wrapper">
        <Tabs tabList={tabData} setFilter={setFilter} />
      </div>
    </div>
  );
};

export default HighResOrdersContainer;
